// What each maker chose to put on its owner page. Same platform, different products, so a different page each time:
// the mix, the order and the shape of the modules change with what that owner actually does with the thing.
// A unit with no entry here gets the default page (hero, live card, three rows, ask bar).
// Every value is invented for illustration.
import type { IconName } from './icons';

export type Head = {
  title?: string; // defaults to the full model name
  status?: string; // defaults to phoneData().status
  side?: 'thumb' | 'lock'; // a product thumbnail or a lock switch beside the title
};

export type Mod =
  | { t: 'hero' } // product photo with callouts (phoneData)
  | { t: 'photo'; tag: string } // a plain product strip with one tag
  | { t: 'live' } // stat card with sparkline (phoneData)
  | { t: 'rows'; items: { icon: IconName; t: string; sub: string }[] }
  | { t: 'countdown'; k: string; big: string; unit: string; sub: string; pct: number; icon: IconName }
  | { t: 'checklist'; title: string; meta: string; items: { t: string; done: boolean; note?: string }[] }
  | { t: 'holder'; k: string; who: string; sub: string; badge: string }
  | { t: 'actions'; items: { icon: IconName; t: string; sub: string; alert?: boolean }[] }
  | { t: 'bars'; k: string; value: string; unit: string; sub: string; days: number[] }
  | { t: 'tiles'; items: { k: string; v: string; unit?: string; ring?: number }[] }
  | { t: 'transfer'; title: string; sub: string }
  | { t: 'flow'; solar: string; home: string; grid: string; charge: number }
  | { t: 'contact'; k: string; who: string; sub: string }
  | { t: 'video'; title: string; sub: string }
  | { t: 'reorder'; title?: string; items: { t: string; sub: string; due?: boolean }[]; cta: string }
  | { t: 'dial'; temp: number; target: string; sub: string }
  | { t: 'readings'; title: string; meta: string; items: { k: string; v: string; at: number; warn?: boolean }[] }
  | { t: 'book'; title: string; sub: string; slots: string[] }
  | { t: 'ask'; text: string; style: 'bar' | 'chip' | 'prompt' };

export type Composition = { head: Head; mods: Mod[] };

export const COMPOSE: Partial<Record<string, Composition>> = {
  // A hire fleet machine. The person holding the phone is an operator on site or a depot fitter.
  lift: {
    head: { title: 'TX12', status: 'Fleet no. 0417 · safe to use', side: 'thumb' },
    mods: [
      { t: 'countdown', k: 'Thorough exam', big: '138', unit: 'days', sub: 'Due 14 Mar · Pennine', pct: 0.62, icon: 'shield' },
      {
        t: 'checklist',
        title: 'Pre-use check',
        meta: 'Today 07:42 · J. Okafor',
        items: [
          { t: 'Guard rails and gate latch', done: true },
          { t: 'Emergency lowering', done: true },
          { t: 'Tyres and pothole guard', done: true },
          { t: 'Platform controls and alarm', done: true },
        ],
      },
      { t: 'holder', k: 'On hire to', who: 'Kirkgate site', sub: 'Wakefield · since 3 Oct', badge: 'Day 24' },
      {
        t: 'actions',
        items: [
          { icon: 'alert', t: 'Report a fault', sub: 'Photo and location', alert: true },
          { icon: 'return', t: 'Off-hire', sub: 'Back to depot' },
        ],
      },
      { t: 'ask', text: 'Stuck at height? How to lower it', style: 'chip' },
    ],
  },

  // A homeowner's garden machine. Spring is when it matters.
  mower: {
    head: { status: 'Parked · battery charged' },
    mods: [
      { t: 'hero' },
      {
        t: 'checklist',
        title: 'Ready for the season',
        meta: '3 of 4',
        items: [
          { t: 'Warranty registered', done: true, note: 'To Mar 2029' },
          { t: 'Battery charged', done: true, note: '12.6 V' },
          { t: 'Tyres at 14 psi', done: true },
          { t: 'Blades sharpened', done: false, note: 'Due' },
        ],
      },
      { t: 'reorder', items: [{ t: 'Blade set', sub: 'Fits this RM-42 · Fenwick', due: true }], cta: 'Order' },
      { t: 'ask', text: 'Ask Moorcroft', style: 'bar' },
    ],
  },

  // A rider's bike: numbers first, then the battery, then what happens when it is sold.
  ebike: {
    head: { title: 'T8', status: 'Frame VT8-26-18842', side: 'lock' },
    mods: [
      { t: 'photo', tag: 'Registered to Maya' },
      { t: 'bars', k: 'This week', value: '64', unit: 'km', sub: 'Mostly eco · longest ride 15 km', days: [8, 12, 0, 9, 14, 6, 15] },
      {
        t: 'tiles',
        items: [
          { k: 'Battery health', v: '94', unit: '%', ring: 0.94 },
          { k: 'Range', v: '82', unit: 'km' },
          { k: 'Service in', v: '300', unit: 'km' },
        ],
      },
      { t: 'transfer', title: 'Selling it?', sub: 'Transfer it to the buyer' },
      { t: 'ask', text: 'Ask Velora', style: 'bar' },
    ],
  },

  // A home battery: the owner wants to see where the power is going, and who to call.
  battery: {
    head: { status: 'Charging from solar' },
    mods: [
      { t: 'flow', solar: '3.1 kW', home: '1.2 kW', grid: '0.4 kW out', charge: 78 },
      {
        t: 'tiles',
        items: [
          { k: 'From the sun today', v: '86', unit: '%' },
          { k: 'Backup reserve', v: '20', unit: '%' },
        ],
      },
      { t: 'countdown', k: 'Warranty', big: '9 yrs 5 mths', unit: 'left', sub: 'Capacity 98% · guaranteed above 70%', pct: 0.94, icon: 'shield' },
      { t: 'contact', k: 'Your installer', who: 'Northgate Solar', sub: 'Fitted 14 Apr 2026' },
      { t: 'ask', text: 'Will it cover a power cut?', style: 'chip' },
    ],
  },

  // A café's espresso machine: the next job, how to do it, and what to reorder.
  coffee: {
    head: { title: 'Good morning, Tidewater', status: 'Ready · 9 bar · 212 shots today' },
    mods: [
      { t: 'countdown', k: 'Descale due', big: '4', unit: 'days', sub: 'About 20 minutes, before opening', pct: 0.86, icon: 'drop' },
      { t: 'video', title: 'How to descale the E2', sub: '3 min · step by step' },
      {
        t: 'reorder',
        title: 'Running low',
        items: [
          { t: 'Water filter', sub: 'In 3 weeks', due: true },
          { t: 'Group gaskets', sub: 'Fits this E2' },
        ],
        cta: 'Reorder',
      },
      { t: 'contact', k: 'Your engineer', who: 'Tamperline', sub: 'Six-month service · 16 Feb' },
      { t: 'ask', text: 'Shots running fast this morning?', style: 'prompt' },
    ],
  },

  // A garden hot tub: water first, then the bits that keep it clean.
  hottub: {
    head: { title: 'The Old Barn', status: 'Calder S6' },
    mods: [
      { t: 'dial', temp: 38, target: 'Ready', sub: 'Cover on · 6°C outside' },
      {
        t: 'readings',
        title: 'Water',
        meta: 'Tested Sunday',
        items: [
          { k: 'pH', v: '7.4', at: 0.5 },
          { k: 'Chlorine', v: '4 ppm', at: 0.62 },
          { k: 'Alkalinity', v: '140 ppm', at: 0.86, warn: true },
        ],
      },
      { t: 'reorder', items: [{ t: 'Filter pair', sub: 'Due in 2 weeks', due: true }], cta: 'Order' },
      { t: 'book', title: 'Spring service', sub: 'Westbrook Spas', slots: ['Thu 8 May', 'Sat 10 May', 'Tue 13 May'] },
      { t: 'ask', text: 'Ask about water care', style: 'bar' },
    ],
  },
};

// ---------- The passport phone: who it is for and what it shows follows each product's rules ----------
export const PASSPORT_TABS: Partial<Record<string, string[]>> = {
  lift: ['Public', 'Inspector', 'Repairer', 'Recycler'],
  mower: ['Public', 'Dealer', 'Recycler', 'Authority'],
  ebike: ['Rider', 'Repairer', 'Recycler', 'Authority'],
  battery: ['Public', 'Installer', 'Recycler', 'Authority'],
  coffee: ['Public', 'Engineer', 'Recycler'],
  hottub: ['Public', 'Repairer', 'Recycler'],
};

export const PASSPORT_NOTE: Partial<Record<string, string>> = {
  lift: 'Machinery Regulation fields. Inspectors see every examination on this serial.',
  mower: 'Machinery Regulation fields. Dealers see the parts and service kits.',
  ebike: 'Battery Regulation fields for light means of transport. Each role sees only what it may.',
  battery: 'Battery Regulation fields. Recyclers see the chemistry and how to make it safe.',
};

/** An icon for a passport row, from what the row is about. */
export function passportIcon(row: string): IconName {
  const r = row.toLowerCase();
  if (r.includes('battery') || r.includes('charge')) return 'bolt';
  if (r.includes('examination')) return 'cal';
  if (r.includes('spare') || r.includes('repair')) return 'tool';
  if (r.includes('energy') || r.includes('carbon') || r.includes('refrigerant') || r.includes('noise')) return 'leaf';
  if (r.includes('conformity') || r.includes('certificate') || r.includes('safety')) return 'shield';
  return 'doc';
}
